import React, { useMemo, useState } from "react";
import {
  IconButton,
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useSelector } from "react-redux";
import { transformSourceActionsData } from "../../../../utils/actions-helpers";
import EmptyView from "../../../../Component/EmptyView/EmptyView";
import MUIButton from "../../../../Component/UI/MUIButton/MUIButton";
import FilterSelection from "../../common/FilterSelection";
import { prepareSelectOptions } from "./Actions";
import { useEffect } from "react";
import { dynamicReplace } from "./ActionModal";

const headers = ["Source", "Full URLs", "Actions", "Case"];

const TopSourceActions = () => {
  const actionMetrics = useSelector((state) => state.actionMetrics);
  const actionMetricsData = actionMetrics?.data?.actionList;

  const [modalData, setModalData] = useState(null);
  const [visibleRows, setVisibleRows] = useState(10);
  const [filters, setFilters] = useState({
    brand: [],
    product: [],
    llm: [],
    market: [],
    geo: [],
    persona: [],
  });

  useEffect(() => {
    if (actionMetricsData && actionMetricsData.length > 0) {
      const options = prepareSelectOptions(
        actionMetricsData?.map((item) => item?.action_json)
      );

      const defaultLlm = options.llm?.some((item) => item.value === "gpt-4o")
        ? ["gpt-4o"]
        : [options.llm?.[0]?.value || ""];

      const defaultGeo = options.geo?.some((item) => item.value === "USA")
        ? ["USA"]
        : [options.geo?.[0]?.value || ""];

      const defaultMarket = options.market?.some(
        (item) => item.value === "General Market"
      )
        ? ["General Market"]
        : [options.market?.[0]?.value || ""];

      const defaultPersona = options.persona?.some(
        (item) => item.value === "General Persona"
      )
        ? ["General Persona"]
        : [options.persona?.[0]?.value || ""];

      setFilters({
        brand: [options?.brand?.[0]?.value || ""],
        product: [],
        llm: defaultLlm,
        market: defaultMarket,
        geo: defaultGeo,
        persona: defaultPersona,
      });
    }
  }, [actionMetrics]);

  const applyFilters = (data) => {
    return data?.filter((data) => {
      const item = data?.action_json;
      const isBrandSelected = filters.brand.length > 0;
      const isProductSelected = filters.product.length > 0;

      const isBrandValid =
        isBrandSelected &&
        !item.isProduct &&
        filters.brand.includes(item?.brand);

      const isProductValid =
        isProductSelected &&
        item.isProduct &&
        filters.product.includes(item?.brand);

      const isLlmValid = !filters.llm.length || filters.llm.includes(item?.llm);
      const isMarketValid =
        !filters.market.length || filters.market.includes(item?.market);
      const isGeoValid =
        !filters.geo.length || filters.geo.includes(item?.geography);
      const isPersonaValid =
        !filters.persona.length || filters.persona.includes(item?.persona);

      if (!isBrandSelected && !isProductSelected) {
        return isLlmValid && isMarketValid && isGeoValid && isPersonaValid;
      }

      return (
        (isBrandValid || isProductValid) &&
        isLlmValid &&
        isMarketValid &&
        isGeoValid &&
        isPersonaValid
      );
    });
  };

  const handleShowMore = () => {
    setVisibleRows((prev) => prev + 15);
  };

  const transformedData = transformSourceActionsData(
    applyFilters(actionMetricsData)?.map((item) => item?.action_json)
  );
  let data = transformedData?.sort(
    (a, b) => (b.groupedActions?.length || 0) - (a.groupedActions?.length || 0)
  );

  data = data?.filter(item =>
    item.groupedActions?.some(group => {
      return (group.category || '').toLowerCase().includes('search')
    })
  );

  const maxDomainLength = useMemo(() => {
    return Math.max(...data?.map((row) => row?.source?.length), 10);
  }, [data]);

  if (!actionMetricsData || actionMetricsData.length === 0) {
    return <EmptyView message="Nothing here!" />;
  }

  const optionsMapping = prepareSelectOptions(
    actionMetricsData?.map((item) => item?.action_json)
  );

  const handleFilterChange = (key, selectedOptions) => {
    const values = selectedOptions.map((option) => option.value);
    setFilters((prevFilters) => ({ ...prevFilters, [key]: values }));
  };

  const tableCellWidth = ["23%", "35%", "25%", "20%"];

  return (
    <>
      <div className="row mt-4" style={{ position: "relative", zIndex: 1200 }}>
        {["brand", "product", "llm", "market", "geo", "persona"].map(
          (filterKey) => (
            <div className="col-md-2" key={filterKey}>
              <FilterSelection
                options={optionsMapping[filterKey]}
                type={filterKey}
                selectedOptions={filters[filterKey].map((value) => ({
                  value,
                  label: value,
                }))}
                handleChange={(selectedOptions) =>
                  handleFilterChange(filterKey, selectedOptions)
                }
              />
            </div>
          )
        )}
      </div>
      <p></p>
      <div
        style={{
          maxHeight: "520px",
          overflowY: "auto",
          position: "relative",
        }}
      >
        <table
          className="table table-striped"
          style={{ width: "100%", tableLayout: "fixed" }}
        >
          <thead
            style={{
              position: "sticky",
              top: 0,
              background: "white",
              zIndex: 2,
            }}
          >
            <tr className="prdtblHeader">
              {headers.map((header, index) => (
                <th
                  key={header}
                  style={{
                    width: tableCellWidth[index],
                    textAlign: "left",
                    padding: "10px",
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!!data?.length ? (
              data?.slice(0, visibleRows)?.map((row, index) => {
                const rowHeight = Math.max(row.fullUrls.length * 36, 40);

                return (
                  <tr key={index}>
                    <td style={{ minWidth: `${maxDomainLength}ch` }}>
                      {row.source}
                    </td>
                    <td style={{ textAlign: "left", height: rowHeight }}>
                      {row?.fullUrls?.map((url, i) => (
                        <div
                          key={i}
                          style={{
                            borderBottom:
                              i < row?.fullUrls.length - 1
                                ? "1px solid #ddd"
                                : "",
                            paddingBottom: "4px",
                          }}
                        >
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {url}
                          </a>
                        </div>
                      ))}
                    </td>
                    <td>
                      {row.groupedActions?.map(({ category, actions }, i) => (
                        <>
                          <div key={i} style={{ height: "35px" }}>
                            <span
                              style={{
                                cursor: actions.length > 1 ? "pointer" : "",
                              }}
                              onClick={() =>
                                actions.length > 1 &&
                                setModalData({ category, actions })
                              }
                            >
                              {dynamicReplace(actions[0])}
                            </span>
                          </div>
                          {row?.groupedActions.length - 1 > i && <hr />}
                        </>
                      ))}
                    </td>

                    <td>
                      {row.groupedActions?.map(({ category }, i) => (
                        <>
                          <div key={i} style={{ height: "55px" }}>
                            {category}
                          </div>
                          {row?.groupedActions.length - 1 > i && <hr />}
                        </>
                      ))}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={4}
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {visibleRows < data.length && (
          <div style={{ textAlign: "center", padding: "10px" }}>
            <MUIButton variant="text" onClick={handleShowMore}>
              Load More
            </MUIButton>
          </div>
        )}
      </div>

      <Dialog
        open={!!modalData}
        onClose={() => setModalData(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <div style={{ marginBottom: "12px" }}>
            {/* <p style={{ fontSize: "17px", fontWeight: "bold" }}>
              {modalData?.category}
            </p> */}
            <List>
              <h6>Action Data Full Summary</h6>
              {modalData?.actions.map((action, j) => (
                <ListItem key={j} sx={{ padding: "2px 0" }}>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: "14px" }}
                    primary={`• ${dynamicReplace(action)}`}
                  />
                </ListItem>
              ))}
            </List>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TopSourceActions;
